import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  styled,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  AlertTitle,
  Stack,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { OutreachCategory } from "../../types/outreach";
import { sendEmails } from "../../services/outreach/mailgun/messaging";
import { formatNumber } from "../outreach/OutreachDataGrid";
import { IoIosSend } from "react-icons/io";
import { ButtonSkeleton } from "../common/SkeletonLoaders";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import SendIcon from "@mui/icons-material/Send";
import type { IndividualEmailSendResponse } from "../../services/outreach/types";
import { IndividualEmailResults } from "./IndividualEmailResults";

// Custom styled components for dark theme
const DarkDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "#121212",
    color: "#ffffff",
    backgroundImage:
      "linear-gradient(rgba(65, 200, 196, 0.05), rgba(0, 0, 0, 0.9))",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
    borderRadius: "12px",
    border: "1px solid rgba(65, 200, 196, 0.2)",
  },
}));

const DarkTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: "rgba(30, 30, 30, 0.8)",
  borderRadius: "8px",
  border: "1px solid rgba(65, 200, 196, 0.2)",
  "& .MuiTableHead-root": {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
  "& .MuiTableCell-head": {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "#41c8c4",
    fontWeight: "bold",
    borderBottom: "1px solid rgba(65, 200, 196, 0.3)",
  },
  "& .MuiTableCell-body": {
    color: "#ffffff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: "rgba(65, 200, 196, 0.1)",
  },
}));

const DarkTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    color: "#ffffff",
    backgroundColor: "rgba(30, 30, 30, 0.8)",
    borderRadius: "4px",
  },
  "& .MuiInputLabel-root": {
    color: "#41c8c4",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(65, 200, 196, 0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(65, 200, 196, 0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#41c8c4",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255, 255, 255, 0.7)",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#41c8c4",
  color: "#000000",
  fontWeight: "bold",
  "&:hover": {
    backgroundColor: "#36a9a6",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(65, 200, 196, 0.3)",
    color: "rgba(0, 0, 0, 0.5)",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  color: "#41c8c4",
  borderColor: "#41c8c4",
  "&:hover": {
    borderColor: "#36a9a6",
    backgroundColor: "rgba(65, 200, 196, 0.1)",
  },
}));

const DarkCheckbox = styled(Checkbox)(({ theme }) => ({
  color: "rgba(65, 200, 196, 0.7)",
  "&.Mui-checked": {
    color: "#41c8c4",
  },
}));

interface OutreachEmailModalProps {
  open: boolean;
  onClose: () => void;
  selectedItems: any[];
  category: OutreachCategory;
  visibleColumns: string[];
  onRemoveItem?: (id: string) => void;
  campaignId?: string; // Add campaign ID prop
}

export const OutreachEmailModal: React.FC<OutreachEmailModalProps> = ({
  open,
  onClose,
  selectedItems,
  category,
  visibleColumns,
  onRemoveItem,
  campaignId,
}) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [overrideToAddress, setOverrideToAddress] = useState("");
  const [sending, setSending] = useState(false);
  const [sendPhase, setSendPhase] = useState<"idle" | "preparing" | "sending" | "complete">("idle");
  const [progress, setProgress] = useState(0);
  const [currentRecipientIndex, setCurrentRecipientIndex] = useState(0);
  const [currentRecipientLabel, setCurrentRecipientLabel] = useState<string>("");
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [sendResult, setSendResult] = useState<IndividualEmailSendResponse | null>(null);
  const [estimatedDuration, setEstimatedDuration] = useState<number>(0);
  const [isCancelled, setIsCancelled] = useState(false);
  const cancelRequestedRef = useRef(false);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  const steps = useMemo(
    () => [
      { label: "Preparing", icon: <HourglassBottomIcon fontSize="small" /> },
      { label: "Sending", icon: <SendIcon fontSize="small" /> },
      { label: "Complete", icon: <CheckCircleIcon fontSize="small" /> },
    ],
    []
  );

  const activeStep = useMemo(() => {
    switch (sendPhase) {
      case "preparing":
        return 0;
      case "sending":
        return 1;
      case "complete":
        return 2;
      case "idle":
      default:
        return 0;
    }
  }, [sendPhase]);

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds <= 0) {
      return "< 1s";
    }
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;
    if (minutes === 0) {
      return `${remaining}s`;
    }
    return `${minutes}m ${remaining.toString().padStart(2, "0")}s`;
  };

  // Reset form when modal is opened
  useEffect(() => {
    if (open) {
      setSubject("");
      setBody("");
      setTestMode(false);
      setTestEmail("");
      setOverrideToAddress("");
      setSendResult(null);
      setSendPhase("idle");
      setProgress(0);
      setCurrentRecipientIndex(0);
      setCurrentRecipientLabel("");
      setSuccessCount(0);
      setFailureCount(0);
      setIsCancelled(false);
      cancelRequestedRef.current = false;
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, []);

  // Get column headers based on category and visible columns
  const getColumnHeaders = () => {
    // Get all columns for the category
    const allColumns = getColumnsForCategory();

    // If no visible columns specified, show all columns
    if (!visibleColumns || visibleColumns.length === 0) {
      return allColumns;
    }

    // Return all columns - we'll show all available columns in the modal
    return allColumns;
  };

  // Get columns based on category
  const getColumnsForCategory = () => {
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
        return [
          { field: "channelName", header: "Channel Name" },
          { field: "subscriberCount", header: "Subscriber Count" },
          { field: "viewCount", header: "Total Views" },
          { field: "videoCount", header: "Videos" },
          { field: "likes", header: "Likes" },
          { field: "engagement", header: "Engagement" },
          { field: "genre", header: "Genre" },
          { field: "email", header: "Email" },
          { field: "socialHandles", header: "Social Media" },
        ];
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return [
          { field: "channelName", header: "Channel Name" },
          { field: "subscriberCount", header: "Subscriber Count" },
          { field: "viewCount", header: "Total Views" },
          { field: "videoCount", header: "Videos" },
          { field: "likes", header: "Likes" },
          { field: "engagement", header: "Engagement" },
          { field: "genre", header: "Genre" },
          { field: "email", header: "Email" },
        ];
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return [
          { field: "playlistName", header: "Playlist Name" },
          { field: "playlistLikes", header: "Likes" },
          { field: "playlistFollowers", header: "Followers" },
          { field: "trackCount", header: "Tracks" },
          { field: "genre", header: "Genre" },
          { field: "curatorSocialHandle", header: "Social Media" },
          { field: "curatorEmail", header: "Email" },
        ];
      case OutreachCategory.MUSIC_BLOGS:
        return [
          { field: "blogName", header: "Blog" },
          { field: "contact", header: "Contact" },
          { field: "genre", header: "Genre" },
          { field: "monthlyVisits", header: "Monthly Visits" },
        ];
      default:
        return [];
    }
  };

  // Get email field name based on category
  const getEmailField = () => {
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return "email";
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return "curatorEmail";
      case OutreachCategory.MUSIC_BLOGS:
        return "contact";
      default:
        return "email";
    }
  };

  // Get name field based on category
  const getNameField = () => {
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return "channelName";
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return "playlistName";
      case OutreachCategory.MUSIC_BLOGS:
        return "blogName";
      default:
        return "";
    }
  };

  // Format cell value for display
  const formatCellValue = (item: any, field: string) => {
    if (!item || item[field] === undefined || item[field] === null) {
      return "-";
    }

    // Format based on field type
    if (field === "genre" && typeof item[field] === "string") {
      return item[field].split("/").join(", ");
    }

    // Format number fields
    const numericFields = [
      "subscriberCount",
      "viewCount",
      "videoCount",
      "likes",
      "playlistLikes",
      "playlistFollowers",
      "trackCount",
      "monthlyVisits",
    ];

    if (numericFields.includes(field)) {
      return formatNumber(item[field]);
    }

    return String(item[field]);
  };

  const effectiveRecipients = useMemo(() => {
    const trimmedOverride = overrideToAddress.trim();
    if (trimmedOverride) {
      return trimmedOverride
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean)
        .map((email) => ({ email, name: "" }));
    }

    const emailField = getEmailField();
    const nameField = getNameField();

    return selectedItems
      .filter((item) => item[emailField])
      .map((item) => ({
        email: item[emailField],
        name: item[nameField] || "",
      }));
  }, [overrideToAddress, selectedItems, category]);

  const previewRecipients = useMemo(
    () => effectiveRecipients.slice(0, 3),
    [effectiveRecipients]
  );

  const recipientCount = effectiveRecipients.length;

  useEffect(() => {
    const estimate = Math.max(30, Math.round(Math.max(1, recipientCount) * 2.5));
    setEstimatedDuration(estimate);
  }, [recipientCount]);

  const largeListWarning = recipientCount > 50;

  const estimatedDurationLabel = useMemo(
    () => formatDuration(estimatedDuration),
    [estimatedDuration]
  );

  const progressDescription = useMemo(() => {
    if (isCancelled) {
      return "Sending cancelled";
    }
    switch (sendPhase) {
      case "preparing":
        return "Preparing individualized messages...";
      case "sending":
        return currentRecipientLabel
          ? `Sending to ${currentRecipientLabel}...`
          : "Sending individual emails...";
      case "complete":
        return sendResult?.success
          ? "Batch send completed successfully"
          : "Batch send completed with issues";
      case "idle":
      default:
        return "Ready to send individually.";
    }
  }, [sendPhase, currentRecipientLabel, sendResult, isCancelled]);

  const canSend = useMemo(() => {
    if (!subject.trim() || !body.trim()) {
      return false;
    }
    if (testMode) {
      return testEmail.trim() !== "";
    }
    return recipientCount > 0;
  }, [subject, body, testMode, testEmail, recipientCount]);

  const totalRecipientsForDisplay = Math.max(
    recipientCount,
    testMode && !overrideToAddress.trim() ? 1 : 0
  );

  // Handle removing an item
  const handleRemoveItem = (id: string) => {
    if (onRemoveItem) {
      onRemoveItem(id);
    }
  };

  const handleCancelSend = () => {
    if (sending) {
      setIsCancelled(true);
      cancelRequestedRef.current = true;
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setSendPhase("idle");
      setProgress(0);
      setCurrentRecipientLabel("Sending cancelled");
      setCurrentRecipientIndex(0);
      setSending(false);
    }
  };

  // Handle sending emails
  const handleSendEmails = async () => {
    if (sending) {
      return;
    }

    if (!subject || !body) {
      setSendResult({
        success: false,
        message: "Subject and body are required",
        errors: ["Missing subject or body"],
      });
      return;
    }

    if (testMode && !testEmail) {
      setSendResult({
        success: false,
        message: "Test email address is required when in test mode",
        errors: ["Missing test email"],
      });
      return;
    }

    if (!testMode && effectiveRecipients.length === 0) {
      setSendResult({
        success: false,
        message: "No recipients available for individual send",
        errors: ["No recipients"],
      });
      return;
    }

    setSending(true);
    setSendResult(null);
    setSendPhase("preparing");
    setProgress(5);
    setCurrentRecipientIndex(0);
    setCurrentRecipientLabel(effectiveRecipients[0]?.email || "");
    setSuccessCount(0);
    setFailureCount(0);
    setIsCancelled(false);
    cancelRequestedRef.current = false;

    try {
      const recipients = effectiveRecipients;
      const override = overrideToAddress.trim() !== "" ? overrideToAddress : undefined;
      const recipientsForProgress = override
        ? override.split(",").map((email) => email.trim()).filter(Boolean)
        : recipients.map((recipient) => recipient.email);

      const totalRecipients =
        recipientsForProgress.length > 0
          ? recipientsForProgress.length
          : recipients.length;

      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }

      let simulatedIndex = 0;
      const intervalDuration = Math.max(
        500,
        Math.round((estimatedDuration * 1000) / Math.max(totalRecipients || 1, 1))
      );

      progressTimerRef.current = setInterval(() => {
        simulatedIndex = Math.min(totalRecipients, simulatedIndex + 1);
        setCurrentRecipientIndex(simulatedIndex);
        if (recipientsForProgress[simulatedIndex - 1]) {
          setCurrentRecipientLabel(recipientsForProgress[simulatedIndex - 1]);
        }
        if (totalRecipients > 0) {
          const simulatedProgress = Math.min(
            95,
            Math.round((simulatedIndex / totalRecipients) * 90) + 5
          );
          setProgress(simulatedProgress);
        } else {
          setProgress((prev) => (prev < 95 ? prev + 1 : prev));
        }

        if (cancelRequestedRef.current || simulatedIndex >= totalRecipients) {
          if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
          }
        }
      }, intervalDuration);

      setTimeout(() => setSendPhase("sending"), 200);

      const result = await sendEmails({
        recipients,
        subject,
        body,
        testMode,
        testEmail: testMode ? testEmail : undefined,
        overrideToAddress: override,
        campaignId: campaignId || `bulk_${Date.now()}`, // Add campaign ID
      });

      if (cancelRequestedRef.current) {
        setSending(false);
        return;
      }

      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }

      setSendResult(result);
      const summary = result.batchSummary;
      setSuccessCount(summary?.successCount ?? (result.success ? totalRecipients : 0));
      setFailureCount(summary?.failureCount ?? (result.success ? 0 : totalRecipients));
      setProgress(100);
      setCurrentRecipientIndex(summary?.total ?? totalRecipients);
      setCurrentRecipientLabel("Batch completed");
      setSendPhase("complete");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setSendResult({
        success: false,
        message,
        errors: [message],
      });
      setFailureCount((prev) => Math.max(prev, effectiveRecipients.length || 1));
      setProgress(100);
      setSendPhase("complete");
    } finally {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setSending(false);
    }
  };

  // Get name field based on category type
  const getNameFieldForDisplay = () => {
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return "channelName";
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return "playlistName";
      case OutreachCategory.MUSIC_BLOGS:
        return "blogName";
      default:
        return "name";
    }
  };

  // Get header text based on category type
  const getNameHeaderText = () => {
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return "Channel Name";
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return "Playlist Name";
      case OutreachCategory.MUSIC_BLOGS:
        return "Blog Name";
      default:
        return "Name";
    }
  };

  return (
    <DarkDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: "rgba(0,0,0,0.7)",
          borderBottom: "1px solid rgba(65, 200, 196, 0.3)",
        }}
      >
        <Stack spacing={1}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
            <Chip
              icon={<PrivacyTipIcon sx={{ color: "#41c8c4" }} />}
              label="Individual Send Mode"
              sx={{
                bgcolor: "rgba(65, 200, 196, 0.15)",
                color: "#41c8c4",
                fontWeight: 700,
              }}
            />
            <Chip
              icon={<CheckCircleIcon sx={{ color: "#2e7d32" }} />}
              label="Privacy Protected"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
            Each recipient receives a personalized email with full privacy compliance and enhanced tracking.
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                "& .MuiStepLabel-label": { color: "rgba(255,255,255,0.7)" },
                "& .MuiStepIcon-root": { color: "rgba(65, 200, 196, 0.4)" },
                "& .MuiStepIcon-root.Mui-active": { color: "#41c8c4" },
                "& .MuiStepIcon-root.Mui-completed": { color: "#41c8c4" },
              }}
            >
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {step.icon}
                      <Typography variant="body2" sx={{ color: "inherit", fontWeight: 600 }}>
                        {step.label}
                      </Typography>
                    </Stack>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor:
                      sendPhase === "complete" && sendResult?.success ? "#4caf50" : "#41c8c4",
                  },
                }}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                  {progressDescription}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccessTimeIcon fontSize="small" sx={{ color: "rgba(65,200,196,0.7)" }} />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                    Estimated duration: ~{estimatedDurationLabel}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                <Chip label={`Success: ${successCount}`} color="success" size="small" />
                <Chip
                  label={`Failed: ${failureCount}`}
                  color={failureCount > 0 ? "error" : "default"}
                  size="small"
                />
                <Chip
                  label={`Processed: ${currentRecipientIndex}/${totalRecipientsForDisplay}`}
                  variant="outlined"
                  size="small"
                  sx={{ color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.3)" }}
                />
              </Stack>
            </Box>
          </Box>

          {largeListWarning && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{
                backgroundColor: "rgba(255, 183, 77, 0.1)",
                border: "1px solid rgba(255, 183, 77, 0.3)",
                color: "#ffb74d",
              }}
            >
              <AlertTitle>Large recipient list detected</AlertTitle>
              Sending to {recipientCount} recipients individually may take several minutes due to rate limiting safeguards.
            </Alert>
          )}

          {isCancelled && (
            <Alert
              severity="info"
              icon={<CancelScheduleSendIcon />}
              sx={{
                backgroundColor: "rgba(33, 150, 243, 0.1)",
                border: "1px solid rgba(33, 150, 243, 0.4)",
                color: "#90caf9",
              }}
            >
              <AlertTitle>Sending cancelled</AlertTitle>
              Progress tracking has stopped. Messages already submitted to the email service may still deliver.
            </Alert>
          )}

          {sendResult && (
            <Alert
              severity={sendResult.success ? "success" : "error"}
              sx={{
                backgroundColor: sendResult.success
                  ? "rgba(46, 125, 50, 0.2)"
                  : "rgba(211, 47, 47, 0.2)",
                color: sendResult.success ? "#81c784" : "#ef9a9a",
                border: sendResult.success
                  ? "1px solid rgba(46, 125, 50, 0.5)"
                  : "1px solid rgba(211, 47, 47, 0.5)",
              }}
            >
              {sendResult.message}
            </Alert>
          )}

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            divider={<Divider flexItem orientation="vertical" sx={{ borderColor: "rgba(65,200,196,0.1)" }} />}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" gutterBottom sx={{ color: "#41c8c4" }}>
                Recipients ({recipientCount})
              </Typography>
              <DarkTableContainer sx={{ maxHeight: 360 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{getNameHeaderText()}</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{formatCellValue(item, getNameFieldForDisplay())}</TableCell>
                        <TableCell>{formatCellValue(item, getEmailField())}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Remove from selection">
                            <IconButton
                              size="small"
                              sx={{
                                color: "rgba(255, 80, 80, 0.8)",
                                "&:hover": {
                                  color: "#ff5050",
                                  backgroundColor: "rgba(255, 80, 80, 0.1)",
                                },
                              }}
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DarkTableContainer>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ color: "rgba(255,255,255,0.8)", mb: 1 }}>
                  Personalization Preview
                </Typography>
                <Stack spacing={1}>
                  {previewRecipients.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                      No recipients available for preview.
                    </Typography>
                  ) : (
                    previewRecipients.map((recipient, index) => (
                      <Chip
                        key={`${recipient.email}-${index}`}
                        label={`${recipient.name || recipient.email} <${recipient.email}>`}
                        variant="outlined"
                        sx={{ color: "rgba(255,255,255,0.85)", borderColor: "rgba(65,200,196,0.4)" }}
                      />
                    ))
                  )}
                  {recipientCount > previewRecipients.length && (
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                      + {recipientCount - previewRecipients.length} more recipients
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" gutterBottom sx={{ color: "#41c8c4" }}>
                Message Details
              </Typography>
              <DarkTextField
                label="To (Optional - overrides recipients)"
                fullWidth
                margin="normal"
                value={overrideToAddress}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOverrideToAddress(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                variant="outlined"
                helperText="Leave empty to send to all selected recipients"
              />
              <DarkTextField
                label="Subject"
                fullWidth
                margin="normal"
                value={subject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
                required
                variant="outlined"
              />
              <DarkTextField
                label="Body"
                fullWidth
                margin="normal"
                value={body}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBody(e.target.value)}
                multiline
                rows={10}
                required
                variant="outlined"
              />
              <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <DarkCheckbox
                      checked={testMode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestMode(e.target.checked)}
                    />
                  }
                  label={<Typography sx={{ color: "#ffffff" }}>Test Mode</Typography>}
                />
                {testMode && (
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <DarkTextField
                      label="Test Email Address"
                      fullWidth
                      size="small"
                      value={testEmail}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestEmail(e.target.value)}
                      required
                      type="email"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>

          <IndividualEmailResults result={sendResult} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, backgroundColor: "rgba(0, 0, 0, 0.7)" }}>
        {sendPhase === "sending" && (
          <CancelButton
            onClick={handleCancelSend}
            variant="outlined"
            startIcon={<CancelScheduleSendIcon />}
            sx={{ mr: "auto" }}
          >
            Cancel Sending
          </CancelButton>
        )}
        <CancelButton onClick={onClose} variant="outlined">
          Close
        </CancelButton>
        {sending ? (
          <ButtonSkeleton width={160} height={36} />
        ) : (
          <ActionButton
            onClick={handleSendEmails}
            variant="contained"
            disabled={!canSend}
            startIcon={<IoIosSend />}
          >
            Send Individually
          </ActionButton>
        )}
      </DialogActions>
    </DarkDialog>
  );
};
